import {
    useEffect,
    useMemo,
    useState,
    type PropsWithChildren,
    type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../Components/ui/Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../Components/ui/Dialog';
import { TooltipProvider } from '../Components/ui/Tooltip';
import AppHeader from '../Components/layout/AppHeader';
import AppSidebar from '../Components/layout/AppSidebar';
import AppTopNav from '../Components/layout/AppTopNav';
import NavChrome from '../Components/layout/NavChrome';
import PagePreloader from '../Components/navigation/PagePreloader';
import { useIsMobileShell } from '../hooks/useMediaQuery';
import { useLayoutPreferences } from '../hooks/useLayoutPreferences';
import { useSyncNexusTheme } from '../hooks/useSyncNexusTheme';
import { useNexusNavigationLoading } from '../hooks/useNexusNavigationLoading';
import { cn } from '../Components/ui/utils';

type NexusLayoutProps = PropsWithChildren<{
    title?: string;
}>;

export default function NexusLayout({
    title,
    children,
}: NexusLayoutProps) {
    const location = useLocation();
    const { user, logout } = useAuth();
    const isMobile = useIsMobileShell();
    const { isTopNav } = useLayoutPreferences();
    useSyncNexusTheme();
    const [aboutOpen, setAboutOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    /**
     * Desktop + top: esconde sidebar e usa AppTopNav.
     * Mobile: sempre drawer (sidebar), mesmo com preferência `top`.
     */
    const useDesktopTopNav = isTopNav && !isMobile;
    const showSidebar = !useDesktopTopNav;

    const {
        soft,
        pageSkeleton,
        showSkeleton,
        showPreloader,
        showSpinner,
        contentEntering,
        contentDimmed,
    } = useNexusNavigationLoading();

    // Fecha o drawer ao navegar (layout persistente).
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isMobile) {
            setMobileOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (!mobileOpen) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMobileOpen(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [mobileOpen]);

    useEffect(() => {
        if (!isMobile || !mobileOpen) {
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isMobile, mobileOpen]);

    const shellClass = useMemo(
        () =>
            cn(
                'nx-shell',
                !isMobile && collapsed && showSidebar && 'nx-shell--collapsed',
                useDesktopTopNav && 'nx-shell--top-nav',
                isMobile && 'nx-shell--mobile',
                isMobile && mobileOpen && 'nx-shell--mobile-open',
            ),
        [collapsed, isMobile, mobileOpen, showSidebar, useDesktopTopNav],
    );

    const effectiveCollapsed = isMobile ? false : collapsed;

    return (
        <TooltipProvider delayDuration={280}>
            <div className={shellClass}>
                {isMobile ? (
                    <button
                        type="button"
                        className={cn(
                            'nx-sidebar-overlay',
                            mobileOpen && 'nx-sidebar-overlay--open',
                        )}
                        aria-label="Fechar menu"
                        tabIndex={mobileOpen ? 0 : -1}
                        onClick={() => setMobileOpen(false)}
                    />
                ) : null}

                {showSidebar ? (
                    <AppSidebar
                        collapsed={effectiveCollapsed}
                        onToggleCollapsed={() => setCollapsed((v) => !v)}
                        isMobile={isMobile}
                        mobileOpen={mobileOpen}
                        onCloseMobile={() => setMobileOpen(false)}
                        onToggleMobile={() => setMobileOpen((v) => !v)}
                    />
                ) : null}

                <div className="nx-main">
                    <AppHeader
                        user={user}
                        onAbout={() => setAboutOpen(true)}
                        onLogout={() => {
                            void logout().finally(() => {
                                window.location.href = '/login';
                            });
                        }}
                        showMenuButton={isMobile}
                        menuOpen={mobileOpen}
                        onMenuClick={() => setMobileOpen((v) => !v)}
                        showBrand={useDesktopTopNav}
                        topNav={useDesktopTopNav ? <AppTopNav /> : null}
                    />
                    <NavChrome pageTitle={title} />

                    <div className="nx-content-stage">
                        <PagePreloader
                            active={showPreloader}
                            soft={soft}
                            spinner={showSpinner}
                        />
                        <main
                            className={cn(
                                'nx-content',
                                contentDimmed && 'nx-content--dimmed',
                                contentEntering && 'nx-content--enter',
                                showSkeleton && 'nx-content--skeleton',
                            )}
                        >
                            {showSkeleton ? pageSkeleton : children}
                        </main>
                    </div>
                </div>

                <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Sobre o SNGPC Web Infor</DialogTitle>
                            <DialogDescription>
                                Sistema de gerenciamento de produtos controlados com
                                frontend React e API Laravel, integrado ao padrão visual
                                Infor Nexus.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="nexus-dialog-body">
                            <p>
                                A interface reúne cadastros, movimentações, estoque,
                                inventários e geração de arquivos para a ANVISA.
                            </p>
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button">Entendi</Button>
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </TooltipProvider>
    );
}

/** Layout persistente Inertia — mantém sidebar/chrome entre páginas Nexus. */
export function nexusPersistentLayout(title?: string) {
    return function NexusPersistentLayout(page: ReactNode) {
        return <NexusLayout title={title}>{page}</NexusLayout>;
    };
}
